<!-- eslint-disable max-len -->
<script setup lang="ts">
import UiStickyBanner from '@/features/UserInterface/components/UiStickyBanner.vue';
import { correctiveNoticeSticky } from '@/features/Page/data/correctiveNotice';
import PageSectionHero from '@/features/PageSection/components/PageSectionHero.vue';
import PageSectionCatalog from '@/features/PageSection/components/PageSectionCatalog.vue';
import UiMedia from '@/features/UserInterface/components/UiMedia.vue';
import UiLink from '@/features/UserInterface/components/UiLink.vue';
import PageSectionContentOnBackground from '@/features/PageSection/components/PageSectionContentOnBackground.vue';
import { useDeviceSize } from '@/composables/useDeviceSize';
import UiLazyComponent from '@/features/UserInterface/components/UiLazyComponent.vue';
import {
  homeHero,
  homeHeroBanner,
  homeSaleBanner,
  homeShowcaseMode,
  homeShowcaseMedia,
  homeShowcaseMediaLink,
  homeShowcaseContent,
  homeShowcaseOptions,
  legalNotice,
} from '@/features/Page/data/globalData';
import { resolveRegionValue } from '@/composables/regionHelpers';
import { computed } from 'vue';

const { isMobile, deviceBreakpoint } = useDeviceSize();

const resolvedContent = computed(() => {
  return {
    homeShowcaseMode: resolveRegionValue(homeShowcaseMode),
    homeShowcaseMedia: resolveRegionValue(homeShowcaseMedia),
    homeShowcaseMediaLink: resolveRegionValue(homeShowcaseMediaLink),
    homeShowcaseContent: resolveRegionValue(homeShowcaseContent),
    homeShowcaseOptions: resolveRegionValue(homeShowcaseOptions),
    homeSaleBanner: resolveRegionValue(homeSaleBanner),
    homeHero: resolveRegionValue(homeHero),
    homeHeroBanner: resolveRegionValue(homeHeroBanner),
    legalNotice: resolveRegionValue(legalNotice),
  };
});
</script>

<template>
  <div class="home">
    <UiStickyBanner v-if="resolvedContent.legalNotice" v-bind="correctiveNoticeSticky" />
    <PageSectionHero
      :media="resolvedContent.homeHero.media"
      :content="resolvedContent.homeHero.content"
      :hero-banner="resolvedContent.homeHeroBanner"
      :options="{
        skeletonBackground: false,
        style: {
          image: {
            objectFit: 'cover',
          },
          video: {
            objectFit: 'cover',
          },
        },
      }"
    />
    <div class="home-container catalog">
      <PageSectionCatalog
        :source="{
          type: 'collection',
          data: ['homepage-catalog'],
        }"
        :options="{
          contentPosition: 'left',
        }"
        :content="{
          title: 'Shop our best sellers',
          description: 'Explore our range of fan-favourites',
          button: {
            text: isMobile ? 'All' : 'Shop all',
            to: '/collections/products',
          },
        }"
      />
    </div>

    <UiLazyComponent>
      <div
        class="home__sale-banner"
        :style="{
          backgroundColor: resolvedContent.homeSaleBanner.backgroundColor,
        }"
      >
        <div class="home__sale-banner-content">
          <h2 class="home__sale-banner-title">
            {{ resolvedContent.homeSaleBanner.content.title }}
          </h2>
          <UiLink :to="resolvedContent.homeSaleBanner.content.to">
            <p class="home__sale-banner-description">
              {{ resolvedContent.homeSaleBanner.content.description }}
            </p>
          </UiLink>
        </div>
      </div>
    </UiLazyComponent>

    <UiLazyComponent>
      <div class="home-container">
        <UiLink
          :to="resolvedContent.homeShowcaseMediaLink.to"
          v-if="resolvedContent.homeShowcaseMode === 'media'"
        >
          <UiMedia v-bind="resolvedContent.homeShowcaseMedia[isMobile ? 'mobile' : 'desktop']" />
        </UiLink>
        <PageSectionContentOnBackground
          v-if="resolvedContent.homeShowcaseMode === 'content-on-background'"
          :background="resolvedContent.homeShowcaseContent.background"
          :content="resolvedContent.homeShowcaseContent.content"
          :options="resolvedContent.homeShowcaseOptions[deviceBreakpoint]"
        />
      </div>
    </UiLazyComponent>
  </div>
</template>

<style lang="scss" scoped>
.home {
  position: relative;
  .home-container {
    margin-bottom: 4rem;
    position: relative;
    top: 0;
    @include desktop {
      margin: 0 auto 6rem;
      max-width: 1440px;
    }
    &.catalog {
      padding: 0 var(--padding);
      @include desktop {
        padding: 0;
      }
    }
  }
  .section-hero {
    width: 100%;
    margin-bottom: 4rem;
    position: relative;
    @include tablet {
      padding: 0 var(--padding);
      max-width: calc(1248px + var(--padding) * 2);
      margin: 0 auto 6rem;
    }
  }
  &__sale-banner {
    text-align: center;
    margin-bottom: 4rem;
    @include desktop {
      margin-bottom: 6rem;
    }
    &-content {
      padding: 4.75rem;
      @include desktop {
        padding: 3.2rem;
      }
    }
    &-title {
      width: 100%;
      margin-bottom: 0.5rem;
      @include header-2($size: tiny, $color: rgb(var(--white)));
      @include tablet {
        margin-bottom: 1rem;
      }
      @include desktop {
        font-size: 2rem;
      }
    }
    &-description {
      width: 100%;
      text-decoration: underline;
      cursor: pointer;
      @include default-body-text(light);
      font-weight: 300;
    }
  }
}
</style>
